/**
 * Amount entry and display for the ledger UI (P4-T2).
 *
 * Money on the wire is a canonical **string** of exact minor units (`@saldo/contracts`,
 * register §3), so the browser never produces a floating-point amount: a typed `12.34`
 * becomes the string `"1234"`, and nothing in between is a `number`. The formatting side
 * is the mirror of that rule — the UI prints exact minor units, and the sign is shown
 * rather than left to colour alone (UQ-06: non-colour status cues).
 */

import { isWithinIndividualBound } from '@saldo/domain';

/** An optional sign, up to 15 integer digits and at most two decimal places. */
const AMOUNT = /^(-?)(\d{1,15})(?:\.(\d{1,2}))?$/;

export interface ParseAmountOptions {
  /** Whether a leading `-` is accepted (an opening is signed or zero; an ordinary is not). */
  readonly allowNegative: boolean;
  /** Whether `0` is accepted (an opening may be zero; an ordinary income/expense may not). */
  readonly allowZero: boolean;
}

/**
 * Parse typed text into a canonical minor-unit string, or `null` when it is not a usable
 * amount.
 *
 * `null` is a refusal, never a coercion: an empty field, `1,50`, `12.345`, `1e3` and an
 * amount outside the individual bound all fail here rather than becoming a different
 * stored amount.
 */
export function parseAmountToMinor(text: string, options: ParseAmountOptions): string | null {
  const match = AMOUNT.exec(text.trim());
  if (match === null) return null;
  const negative = match[1] === '-';
  if (negative && !options.allowNegative) return null;

  const units = BigInt(match[2] as string);
  const fraction = BigInt((match[3] ?? '').padEnd(2, '0') || '0');
  const minor = units * 100n + fraction;
  if (minor === 0n && !options.allowZero) return null;
  if (!isWithinIndividualBound(negative ? -minor : minor)) return null;
  if (minor === 0n) return '0';
  return `${negative ? '-' : ''}${minor.toString()}`;
}

/** `1250` minor units of CAD as `"12.50"` — the form the editor's field holds. */
export function formatAmountInput(minor: bigint): string {
  const negative = minor < 0n;
  const magnitude = negative ? -minor : minor;
  const units = magnitude / 100n;
  const fraction = magnitude % 100n;
  return `${negative ? '-' : ''}${units.toString()}.${fraction.toString().padStart(2, '0')}`;
}

const INTEGER = new Intl.NumberFormat('en-CA', { maximumFractionDigits: 0 });

/**
 * An exact amount with its currency and its sign: `"−CAD 1,250.00"`.
 *
 * The sign is part of the text because a balance may be negative (debt) and colour must
 * not be the only thing saying so.
 */
export function formatMoney(minor: bigint, currency: string): string {
  const sign = minor < 0n ? '\u2212' : minor > 0n ? '+' : '';
  return `${sign}${formatMoneyMagnitude(minor, currency)}`;
}

/** An exact magnitude with its currency and no sign — for a filter subtotal, never a balance. */
export function formatMoneyMagnitude(minor: bigint, currency: string): string {
  const magnitude = minor < 0n ? -minor : minor;
  const units = magnitude / 100n;
  const fraction = magnitude % 100n;
  return `${currency} ${INTEGER.format(Number(units))}.${fraction.toString().padStart(2, '0')}`;
}

/**
 * A balance: the magnitude with a leading minus when it is negative, and no plus.
 *
 * A running total is not an amount that "went up" — a `+` in front of a balance reads as a
 * delta and misleads. Negatives keep their sign as text, not just as colour.
 */
export function formatBalance(minor: bigint, currency: string): string {
  return `${minor < 0n ? '\u2212' : ''}${formatMoneyMagnitude(minor, currency)}`;
}
